// Round-3 P0-1: the brand's "Primary call to action". Blog drafts invite readers to do exactly this,
// and the brand's product source states it so the quality gate can verify it. Default: visit the website.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import type { BrandPrimaryActionKind } from "../../../../shared/types";

export type PrimaryActionView = {
  kind: BrandPrimaryActionKind | null;
  url: string | null;
  origin: "owner" | "default" | null;
  saved: { kind: BrandPrimaryActionKind; url: string | null } | null;
  options: { kind: BrandPrimaryActionKind; label: string }[];
};

const selectClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50";

export function PrimaryCallToActionForm({ value, onSave, saving = false, error = null }: {
  value: PrimaryActionView;
  onSave: (input: { kind: BrandPrimaryActionKind | null; url: string | null }) => void;
  saving?: boolean;
  error?: string | null;
}) {
  const [kind, setKind] = useState<BrandPrimaryActionKind>(value.saved?.kind ?? "visit");
  const [url, setUrl] = useState(value.saved?.url ?? "");

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // "Visit the website" with no link is the default: clear the saved choice instead of storing it.
    if (kind === "visit" && !url.trim()) onSave({ kind: null, url: null });
    else onSave({ kind, url: url.trim() || null });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" aria-label="Primary call to action">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Primary call to action</h3>
        <p className="mt-1 text-sm text-slate-600">
          Blog drafts invite readers to do this, and only this. Choose an action readers can really take on your site.
        </p>
      </div>
      <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">Action
        <select className={selectClass} value={kind} disabled={saving} onChange={event => setKind(event.target.value as BrandPrimaryActionKind)}>
          {value.options.map(option => <option key={option.kind} value={option.kind}>{option.label}</option>)}
        </select>
      </label>
      <Input label="Link for this action (optional)" type="url" value={url} disabled={saving} onChange={event => setUrl(event.target.value)}
        placeholder="Defaults to your website URL" />
      <p className="text-xs text-slate-500" data-testid="primary-action-current">
        {value.kind && value.url
          ? `Now: ${value.options.find(option => option.kind === value.kind)?.label ?? value.kind} — ${value.url}${value.origin === "default" ? " (default)" : ""}`
          : "Add your website URL above so drafts have somewhere to send readers."}
      </p>
      {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
      <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save call to action"}</Button>
    </form>
  );
}

export function PrimaryCallToAction({ brandId }: { brandId: string }) {
  const qc = useQueryClient();
  const key = ["brands", brandId, "primary-action"];
  const query = useQuery({ queryKey: key, queryFn: () => api.get<{ data: PrimaryActionView }>(`/api/brands/${brandId}/primary-action`) });
  const save = useMutation({
    mutationFn: (input: { kind: BrandPrimaryActionKind | null; url: string | null }) =>
      api.put<{ data: PrimaryActionView }>(`/api/brands/${brandId}/primary-action`, input),
    onSuccess: (result) => { qc.setQueryData(key, result); },
  });
  if (query.isLoading) return <p className="text-sm text-slate-500">Loading call to action…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red-700">Could not load the primary call to action.</p>;
  return (
    <PrimaryCallToActionForm
      key={`${query.data.data.saved?.kind ?? "default"}:${query.data.data.saved?.url ?? ""}`}
      value={query.data.data}
      saving={save.isPending}
      error={save.error instanceof Error ? save.error.message : null}
      onSave={input => save.mutate(input)}
    />
  );
}
